import { RejectedService } from '../model/create-order.model';
import { GraphQLClientError } from './graphql-error.utils';

export type UserMessageAction = 'retry' | 'refresh' | 'none';

export interface UserMessage {
  title: string;
  body: string;
  contact?: string;
  action?: UserMessageAction;
}

/** Solo UX: qué hacer en el front. El texto lo manda el back en userMessage. */
export const ORDER_ERROR_ACTIONS: Record<string, UserMessageAction> = {
  ORDER_NOT_FOUND: 'refresh',
  ORDER_ALREADY_DELETED: 'refresh',
  ORDER_NOT_EDITABLE: 'refresh',
  PDF_RETRY_NOT_ALLOWED: 'refresh',
  PDF_GENERATION_FAILED: 'retry',
  CREATE_ORDER_JOB_NOT_FOUND: 'retry',
  CREATE_ORDER_JOB_ID_REQUIRED: 'retry',
  CREATE_ORDER_JOB_FAILED: 'retry',
  TIMEOUT_CREATE_ORDER_JOB: 'retry',
};

const TIMEOUT_CREATE_ORDER_JOB_MESSAGE: UserMessage = {
  title: 'Tiempo de espera agotado',
  body: 'La creación de órdenes está tardando más de lo esperado. Reintenta o contacta a Soporte TI.',
  action: 'retry',
};

const DEFAULT_ERROR_FALLBACK_TEXT =
  'Ocurrió un error inesperado. Contacta a Soporte TI.';

export function getOrderErrorAction(
  errorCodeString: string | null | undefined,
): UserMessageAction {
  const normalizedCode = errorCodeString?.trim().toUpperCase();
  if (!normalizedCode) {
    return 'none';
  }
  return ORDER_ERROR_ACTIONS[normalizedCode] ?? 'none';
}

function lookupCatalogMessage(
  errorCodeString: string | null | undefined,
  messageCatalogsArray: Record<string, UserMessage>[],
): UserMessage | null {
  const normalizedCode = errorCodeString?.trim().toUpperCase();
  if (!normalizedCode) {
    return null;
  }

  for (const catalogRecord of messageCatalogsArray) {
    if (catalogRecord[normalizedCode]) {
      return catalogRecord[normalizedCode];
    }
  }

  return null;
}

export function resolveUserMessage(
  errorCodeString: string | null | undefined,
  messageCatalogsArray: Record<string, UserMessage>[] = [],
  fallbackBodyText: string = DEFAULT_ERROR_FALLBACK_TEXT,
  backendUserMessage?: string | null,
): UserMessage {
  const normalizedCode = errorCodeString?.trim().toUpperCase();
  const fromBackend = backendUserMessage?.trim();
  const catalogMessage = lookupCatalogMessage(
    errorCodeString,
    messageCatalogsArray,
  );
  const action = getOrderErrorAction(errorCodeString);

  if (normalizedCode === 'TIMEOUT_CREATE_ORDER_JOB' && !fromBackend) {
    return TIMEOUT_CREATE_ORDER_JOB_MESSAGE;
  }

  if (fromBackend) {
    return {
      title: catalogMessage?.title ?? '',
      body: fromBackend,
      action: catalogMessage?.action ?? action,
    };
  }

  if (catalogMessage) {
    return catalogMessage;
  }

  return {
    title: 'Error',
    body: fallbackBodyText,
    action,
  };
}

export function formatUserMessageText(userMessageRecord: UserMessage): string {
  const bodyText = userMessageRecord.body?.trim() || '';
  const contactText = userMessageRecord.contact?.trim() || '';

  if (bodyText && contactText) {
    return `${bodyText} ${contactText}`;
  }

  return bodyText || contactText || DEFAULT_ERROR_FALLBACK_TEXT;
}

export function resolveCreateOrderRejectionMessage(
  rejectedServiceRecord: RejectedService,
): UserMessage {
  return resolveUserMessage(
    rejectedServiceRecord.reason,
    [],
    'No se pudo generar la orden para este servicio.',
    rejectedServiceRecord.userMessage,
  );
}

export function resolveCreateOrderWarningMessage(warningItem: {
  reason?: string | null;
  userMessage?: string | null;
}): UserMessage {
  return resolveUserMessage(
    warningItem.reason,
    [],
    'Advertencia.',
    warningItem.userMessage,
  );
}

export function resolveOrderGraphqlUserMessage(
  encounteredError: unknown,
  fallbackBodyText: string = DEFAULT_ERROR_FALLBACK_TEXT,
): UserMessage {
  if (encounteredError instanceof GraphQLClientError) {
    const firstGraphQLError = encounteredError.graphQLErrors?.[0];
    const extractedCode =
      firstGraphQLError?.extensions?.code ||
      firstGraphQLError?.extensions?.response?.code ||
      null;
    const backendUserMessage =
      typeof firstGraphQLError?.extensions?.userMessage === 'string'
        ? firstGraphQLError.extensions.userMessage
        : null;

    return resolveUserMessage(
      extractedCode,
      [],
      fallbackBodyText,
      backendUserMessage,
    );
  }

  if (encounteredError instanceof Error) {
    return resolveUserMessage(null, [], fallbackBodyText);
  }

  return {
    title: 'Error',
    body: fallbackBodyText,
    action: 'none',
  };
}

export function shouldRefreshAfterOrderError(
  encounteredError: unknown,
): boolean {
  const resolvedMessage = resolveOrderGraphqlUserMessage(encounteredError);
  return resolvedMessage.action === 'refresh';
}
